import React, { useEffect, useState, useCallback } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { 
    Layout, 
    Tabs, 
    Typography, 
    Button, 
    Input, 
    Switch, 
    Tag, 
    Card, 
    Row, 
    Col, 
    Checkbox, 
    TimePicker,
    Select,
    InputNumber,
    Modal,
    message,
    Alert,
    Tooltip,
    Space,
    theme as antTheme
} from 'antd';
import {
    MessageOutlined,
    CalendarOutlined,
    ApiOutlined,
    InfoCircleOutlined,
    CloseOutlined,
    RocketOutlined
} from '@ant-design/icons';
import moment from 'moment-timezone';
import dayjs from 'dayjs';
import session from '../../utils/session';
import constants from '../../constants';
import {
    getUserSettings,
    setUserSettings,
    getSchedContactsAffectedByUserSetting,
    removeUserIntegration,
    integrateCalendly,
    updateInviteMessage,
    updateNoSlotsMessage,
    updateConfirmedMessage,
    updateCancelledMessage,
    updateFollowupMessages,
    updateEventReminders,
    updateCalendarSettings,
    getAccessToken
} from '../../reducers/integrations/resources';
import {
    getAllApps,
    setAppAsDefault
} from '../../reducers/apps/resources';
import { useTheme } from '../../ThemeContext';
import InterviewMessageInput from './InterviewMessageInput';
import FlowOverview from './FlowOverview';
import CronofyCalendarModal from './CronofyCalendarModal';
import './InterviewInvitesUnified.css';
import PageTourButton from '../guide/PageTourButton';
import { getInterviewInvitesSteps } from '../guide/toursteps';

// Import integration logos
import exchangeLogo from '../../assets/integrations/exchange.png';
import googleLogo from '../../assets/integrations/google.jpg';
import icloudLogo from '../../assets/integrations/icloud.jpg';
import mailchimpLogo from '../../assets/integrations/mailchimp.png';
import office365Logo from '../../assets/integrations/office-365.png';
import outlookLogo from '../../assets/integrations/outlook.png';
import atsConnectLogo from '../../assets/integrations/atsconnect.svg';

const integrationLogos = {
    'exchange': exchangeLogo,
    'exchange.png': exchangeLogo,
    'google': googleLogo,
    'google.jpg': googleLogo,
    'google.png': googleLogo,
    'icloud': icloudLogo,
    'icloud.jpg': icloudLogo,
    'icloud.png': icloudLogo,
    'mailchimp': mailchimpLogo,
    'mailchimp.png': mailchimpLogo,
    'office-365': office365Logo,
    'office-365.png': office365Logo,
    'office365': office365Logo,
    'outlook': outlookLogo,
    'outlook.png': outlookLogo,
    'atsconnect': atsConnectLogo,
    'atsconnect.png': atsConnectLogo,
    'atsconnect.svg': atsConnectLogo
};

const appDescriptions = {
    'exchange': 'Sync your appointments with Microsoft Exchange.',
    'google': 'Sync your appointments with Google Calendar to ensure you never miss a consultation.',
    'icloud': 'Sync your appointments with iCloud Calendar.',
    'mailchimp': 'Connect and manage your Mailchimp account to enhance your email.',
    'office-365': 'Sync your appointments with Microsoft Office 365.',
    'outlook': 'Sync your appointments with Microsoft Outlook Calendar.',
    'zoom': 'Integrate Zoom to facilitate video consultations with your patients.',
    'stripe': 'Manage your payments and billing with Stripe integration.',
    'doxy.me': 'Integrate with popular telehealth platforms to offer remote consultations.',
    'salesforce': "Integrate with CRM systems to manage patient relationships and enhance your practice's.",
    'google-drive': 'Integrate with Google Drive to store and manage patient documents.',
    'loom': 'Integrate with Loom to record and share video consultations and messages.',
    'dropbox': 'Integrate with Dropbox to manage patient files and records securely in the cloud.',
    'facebook': 'Link Facebook account to manage your online presence and patient engagement.',
    'atsconnect': 'Sync candidates and jobs with 50+ ATS & HRIS platforms. Coming soon.'
};

const { Content } = Layout;
const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;
const { Option } = Select;

const InterviewInvitesUnified = (props) => {
    const { isDarkMode } = useTheme();
    const { token } = antTheme.useToken();

    // State for all three tabs
    const [activeTab, setActiveTab] = useState('messaging');
    const [loading, setLoading] = useState(false);
    const [activeSection, setActiveSection] = useState('invite');
    const [currentUserId, setCurrentUserId] = useState(null);

    // Individual loading states for each sub-form
    const [loadingInvite, setLoadingInvite] = useState(false);
    const [loadingNoSlots, setLoadingNoSlots] = useState(false);
    const [loadingConfirmed, setLoadingConfirmed] = useState(false);
    const [loadingCancelled, setLoadingCancelled] = useState(false);
    const [loadingFollowup, setLoadingFollowup] = useState(false);
    const [loadingReminder, setLoadingReminder] = useState(false);
    const [loadingCalendar, setLoadingCalendar] = useState(false);

    // Messaging Tab State
    const [inviteMsg, setInviteMsg] = useState(constants.inviteMessage);
    const [noSlotsAvailableMessage, setNoSlotsAvailableMessage] = useState(constants.noSlotsAvailableMessage);
    const [postEventConfirmationResponseMessage, setPostEventConfirmationResponseMessage] = useState(constants.postEventConfirmationResponseMessage);
    const [cancelledMsg, setCancelledMsg] = useState(constants.cancelledMessage);
    const [scheduledMessageEnabled, setScheduledMessageEnabled] = useState(false);
    const [schedmsgs, setSchedmsgs] = useState([
        { key: 1, msg: '', sched: 0, isActive: true },
        { key: 2, msg: '', sched: 0, isActive: true },
        { key: 3, msg: '', sched: 0, isActive: true }
    ]);

    // Initial state for dirty tracking
    const [initialInviteMsg, setInitialInviteMsg] = useState(constants.inviteMessage);
    const [initialNoSlotsMsg, setInitialNoSlotsMsg] = useState(constants.noSlotsAvailableMessage);
    const [initialConfirmedMsg, setInitialConfirmedMsg] = useState(constants.postEventConfirmationResponseMessage);
    const [initialCancelledMsg, setInitialCancelledMsg] = useState(constants.cancelledMessage);
    const [initialFollowupEnabled, setInitialFollowupEnabled] = useState(false);
    const [initialSchedmsgs, setInitialSchedmsgs] = useState([
        { key: 1, msg: '', sched: 0, isActive: true },
        { key: 2, msg: '', sched: 0, isActive: true },
        { key: 3, msg: '', sched: 0, isActive: true }
    ]);
    const [initialReminderEnabled, setInitialReminderEnabled] = useState(true);
    const [initialReminderTime, setInitialReminderTime] = useState(8);
    const [initialReminderMsg, setInitialReminderMsg] = useState('Hi {{firstname}}. This is a reminder of your upcoming meeting with {{companyname}}. If you need to cancel, or contact us, please respond to this message.');

    const [affectedContacts, setAffectedContacts] = useState([]);
    const [showAffectedWarningModal, setShowAffectedWarningModal] = useState(false);

    // Calendar Tab State
    const defaultSlots = [
        { day: 1, startTime: '09:00 AM', endTime: '05:00 PM' },
        { day: 2, startTime: '09:00 AM', endTime: '05:00 PM' },
        { day: 3, startTime: '09:00 AM', endTime: '05:00 PM' },
        { day: 4, startTime: '09:00 AM', endTime: '05:00 PM' },
        { day: 5, startTime: '09:00 AM', endTime: '05:00 PM' },
    ];
    const [interviewSlots, setInterviewSlots] = useState(defaultSlots);
    const [hrDuration, setHrDuration] = useState(0);
    const [minDuration, setMinDuration] = useState(30);
    const [zone, setZone] = useState('');
    const [confirmationMsg, setConfirmationMsg] = useState('');
    const [eventReminderMsg, setEventReminderMsg] = useState('Hi {{firstname}}. This is a reminder of your upcoming meeting with {{companyname}}. If you need to cancel, or contact us, please respond to this message.');
    const [eventReminderEnabled, setEventReminderEnabled] = useState(true);
    const [eventReminderTime, setEventReminderTime] = useState(8);
    const [maxPerInterviewSlot, setMaxPerInterviewSlot] = useState(1);
    const [includeStoreLocation, setIncludeStoreLocation] = useState(false);
    const [noInterviewsWithinNumber, setNoInterviewsWithinNumber] = useState(0);
    const [noInterviewsWithinPeriod, setNoInterviewsWithinPeriod] = useState('h');
    const [scheduleSequence, setScheduleSequence] = useState('random');

    // Initial state for calendar settings dirty tracking
    const [initialInterviewSlots, setInitialInterviewSlots] = useState(defaultSlots);
    const [initialHrDuration, setInitialHrDuration] = useState(0);
    const [initialMinDuration, setInitialMinDuration] = useState(30);
    const [initialZone, setInitialZone] = useState('');
    const [initialConfirmationMsg, setInitialConfirmationMsg] = useState('');
    const [initialMaxPerSlot, setInitialMaxPerSlot] = useState(1);
    const [initialIncludeLocation, setInitialIncludeLocation] = useState(false);
    const [initialNoInterviewsNumber, setInitialNoInterviewsNumber] = useState(0);
    const [initialNoInterviewsPeriod, setInitialNoInterviewsPeriod] = useState('h');
    const [initialScheduleSequence, setInitialScheduleSequence] = useState('random');

    // Inline validation errors for the calendar form. Keyed by field name.
    // Per-slot-row errors live under `slotRows[index]`; per-day overlap errors
    // (matching the backend's grouping) live under `slotsByDay[dayName]`.
    // `_generic` catches anything we couldn't map to a specific field.
    const [calendarFieldErrors, setCalendarFieldErrors] = useState({});

    const clearCalendarError = useCallback((key) => {
        setCalendarFieldErrors((prev) => {
            if (!(key in prev)) return prev;
            const next = { ...prev };
            delete next[key];
            return next;
        });
    }, []);

    const clearCalendarSlotErrors = useCallback(() => {
        setCalendarFieldErrors((prev) => {
            if (!prev.slotRows && !prev.slotsByDay && !prev.slotsGeneric) return prev;
            const next = { ...prev };
            delete next.slotRows;
            delete next.slotsByDay;
            delete next.slotsGeneric;
            return next;
        });
    }, []);

    // Helper: convert interviewSchedule (per-day multi-window) → flat slot rows
    const scheduleToSlots = useCallback((interviewSchedule) => {
        const slots = [];
        if (!Array.isArray(interviewSchedule)) return slots;
        interviewSchedule.forEach((day) => {
            if (!day.isActive || !Array.isArray(day.windows) || day.windows.length === 0) return;
            day.windows.forEach((w) => {
                slots.push({ day: day.key, startTime: w.startTime, endTime: w.endTime });
            });
        });
        // Sort by day then start time
        slots.sort((a, b) => a.day - b.day || a.startTime.localeCompare(b.startTime));
        return slots.length > 0 ? slots : defaultSlots;
    }, []);

    // Helper: convert flat slot rows → interviewSchedule shape for backend
    const slotsToSchedule = useCallback((slots) => {
        const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
        return dayNames.map((name, key) => {
            const daySlots = slots.filter((s) => s.day === key);
            return {
                key,
                name,
                isActive: daySlots.length > 0,
                windows: daySlots.map((s) => ({ startTime: s.startTime, endTime: s.endTime })),
            };
        });
    }, []);

    // Helper: derive legacy selectedDays/startTime/endTime from slots for backward compat
    const slotsToLegacy = useCallback((slots) => {
        const selectedDays = [false, false, false, false, false, false, false];
        slots.forEach((s) => { selectedDays[s.day] = true; });
        // Envelope: earliest start, latest end across all slots
        let earliest = '11:59 PM';
        let latest = '12:00 AM';
        slots.forEach((s) => {
            if (s.startTime < earliest) earliest = s.startTime;
            if (s.endTime > latest) latest = s.endTime;
        });
        return {
            selectedDays,
            startTime: slots.length > 0 ? earliest : '09:00 AM',
            endTime: slots.length > 0 ? latest : '05:00 PM',
        };
    }, []);

    // Mirrors backend lib/availability.js validateWindows + Joi rules so the
    // user gets per-field feedback before the API roundtrip.
    const validateCalendarForm = useCallback(() => {
        const errors = {};
        const slotRows = {};
        const slotsByDay = {};
        const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

        const toMinutes = (timeStr) => {
            if (!timeStr) return null;
            const m = dayjs(timeStr, 'h:mm A');
            return m.isValid() ? m.hour() * 60 + m.minute() : null;
        };

        if (!Array.isArray(interviewSlots) || interviewSlots.length === 0) {
            errors.slotsGeneric = 'Add at least one availability slot.';
        } else {
            interviewSlots.forEach((slot, i) => {
                const start = toMinutes(slot.startTime);
                const end = toMinutes(slot.endTime);
                if (start == null || end == null) {
                    slotRows[i] = 'Start and end time are required.';
                    return;
                }
                if (end <= start) {
                    slotRows[i] = 'End time must be after start time.';
                }
            });

            // Per-day overlap + max-windows check
            const byDay = new Map();
            interviewSlots.forEach((slot, i) => {
                if (slotRows[i]) return;
                if (!byDay.has(slot.day)) byDay.set(slot.day, []);
                byDay.get(slot.day).push(slot);
            });
            byDay.forEach((daySlots, dayKey) => {
                const dayName = dayNames[dayKey] || `Day ${dayKey}`;
                if (daySlots.length > 6) {
                    slotsByDay[dayName] = 'At most 6 windows per day.';
                    return;
                }
                const sorted = [...daySlots].sort((a, b) => toMinutes(a.startTime) - toMinutes(b.startTime));
                for (let i = 1; i < sorted.length; i += 1) {
                    if (toMinutes(sorted[i].startTime) < toMinutes(sorted[i - 1].endTime)) {
                        slotsByDay[dayName] = 'Time windows must not overlap.';
                        break;
                    }
                }
            });
        }

        if (Object.keys(slotRows).length) errors.slotRows = slotRows;
        if (Object.keys(slotsByDay).length) errors.slotsByDay = slotsByDay;

        if (hrDuration == null) errors.hrDuration = 'Required';
        if (minDuration == null) errors.minDuration = 'Required';
        if (errors.hrDuration == null && errors.minDuration == null
            && (hrDuration || 0) === 0 && (minDuration || 0) === 0) {
            errors.duration = 'Interview duration must be at least 5 minutes.';
        }

        if (maxPerInterviewSlot == null || maxPerInterviewSlot < 1) {
            errors.maxPerInterviewSlot = 'Minimum of 1 candidate per slot.';
        }

        if (!zone) errors.zone = 'Required';

        if (noInterviewsWithinNumber == null) {
            errors.noInterviewsWithinNumber = 'Required';
        }

        return errors;
    }, [interviewSlots, hrDuration, minDuration, maxPerInterviewSlot, zone, noInterviewsWithinNumber]);

    // Map backend error payload (from lib/register-routes.js#L223 + calendar-update.js)
    // back to per-field errors so unknown server-side rules still surface inline.
    const parseCalendarBackendError = useCallback((error) => {
        const out = {};
        const slotsByDay = {};
        const message = (error && (error.message || (error.data && error.data.message))) || '';
        if (!message) {
            out._generic = 'Failed to save calendar settings.';
            return out;
        }

        const humanize = (joiMsg) => {
            if (/must be a number|is required|must be a string|must be a boolean|must be an array/i.test(joiMsg)) {
                return 'Required';
            }
            return joiMsg.replace(/^"\w+"\s*/, '').trim() || joiMsg;
        };

        message.split(/;\s*/).forEach((chunkRaw) => {
            const chunk = chunkRaw.trim();
            if (!chunk) return;

            const payloadMatch = chunk.match(/^payload:\s*"(\w+)"\s*(.+)$/);
            if (payloadMatch) {
                const [, field, rest] = payloadMatch;
                if (field === 'noInterviewsWithin') {
                    out.noInterviewsWithinNumber = humanize(rest);
                } else {
                    out[field] = humanize(rest);
                }
                return;
            }

            const scheduleMatch = chunk.match(/^Invalid interview schedule:\s*(.+)$/i);
            if (scheduleMatch) {
                scheduleMatch[1].split(/;\s*/).forEach((dayChunk) => {
                    const dayMatch = dayChunk.match(/^(\w+):\s*(.+)$/);
                    if (dayMatch) {
                        const [, dayName, msg] = dayMatch;
                        slotsByDay[dayName] = msg.charAt(0).toUpperCase() + msg.slice(1) + (msg.endsWith('.') ? '' : '.');
                    } else if (!out._generic) {
                        out._generic = dayChunk;
                    }
                });
                return;
            }

            out._generic = out._generic ? `${out._generic} ${chunk}` : chunk;
        });

        if (Object.keys(slotsByDay).length) out.slotsByDay = slotsByDay;
        return out;
    }, []);

    // Integrations Tab State
    const [integrationApps, setIntegrationApps] = useState({});
    const [groups, setGroups] = useState([]);
    const [showRemoveModal, setShowRemoveModal] = useState(false);
    const [integApp, setIntegApp] = useState(null);
    const [showComingSoonModal, setShowComingSoonModal] = useState(false);

    // Cronofy Modal State
    const [showCronofyModal, setShowCronofyModal] = useState(false);
    const [cronofyData, setCronofyData] = useState(null);
    const [calendars, setCalendars] = useState([]);

    useEffect(() => {
        loadSettings();
        loadApps();
    }, []);

    useEffect(() => {
        if (props.location) {
            const params = new URLSearchParams(props.location.search);
            const tab = params.get('tab');
            if (tab && ['messaging', 'calendar', 'integrations'].includes(tab)) {
                setActiveTab(tab);
            }

            // Handle OAuth Callback
            const code = params.get('code');
            const state = params.get('state');

            if (code && state) {
                try {
                    const parsedState = JSON.parse(decodeURIComponent(state));
                    const type = parsedState.type;

                    if (type && type.startsWith('cronofy')) {
                        handleCronofyCallback(code, type);
                    }
                } catch (e) {
                    console.error('Failed to parse OAuth state', e);
                }
            }
        }
    }, [props.location]);

    const handleCronofyCallback = async (code, type) => {
        try {
            const response = await getAccessToken(`code=${code}&type=${type}&path=interview-invites`);
            const tokenData = response.data;
            
            setCronofyData(tokenData);
            setCalendars(tokenData.calendars);
            setShowCronofyModal(true);
            
            // Clear URL params but keep the tab
            props.history.replace({
                pathname: props.location.pathname,
                search: '?tab=integrations'
            });
            setActiveTab('integrations');
        } catch (error) {
            message.error('Failed to get access token');
        }
    };

    const handleTabChange = (key) => {
        setActiveTab(key);
        if (props.history && props.location) {
            const params = new URLSearchParams(props.location.search);
            params.set('tab', key);
            props.history.push({
                pathname: props.location.pathname,
                search: params.toString()
            });
        }
    };

    const loadSettings = async () => {
        setLoading(true);
        try {
            const response = await getUserSettings();
            const settings = response.data;

            if (settings) {
                if (settings.userId) {
                    setCurrentUserId(settings.userId);
                }

                // Messaging settings
                const inviteMsgValue = settings.inviteMsg || constants.inviteMessage;
                const noSlotsMsgValue = settings.noSlotsAvailableMessage || constants.noSlotsAvailableMessage;
                const confirmedMsgValue = settings.postEventConfirmationResponseMessage || constants.postEventConfirmationResponseMessage;
                const schedEnabledValue = settings.scheduledMessageEnabled || false;
                
                setInviteMsg(inviteMsgValue);
                setInitialInviteMsg(inviteMsgValue);
                
                setNoSlotsAvailableMessage(noSlotsMsgValue);
                setInitialNoSlotsMsg(noSlotsMsgValue);
                
                setPostEventConfirmationResponseMessage(confirmedMsgValue);
                setInitialConfirmedMsg(confirmedMsgValue);

                const cancelledMsgValue = settings.cancelledMsg || constants.cancelledMessage;
                setCancelledMsg(cancelledMsgValue);
                setInitialCancelledMsg(cancelledMsgValue);

                setScheduledMessageEnabled(schedEnabledValue);
                setInitialFollowupEnabled(schedEnabledValue);

                if (settings.schedmsgs) {
                    const transformed = settings.schedmsgs.map(schedule => ({
                        ...schedule,
                        sched: schedule.sched ? parseInt(schedule.sched.replace('h', '')) : 0
                    }));
                    setSchedmsgs(transformed);
                    setInitialSchedmsgs(JSON.parse(JSON.stringify(transformed)));
                }

                // Calendar settings — prefer interviewSchedule (per-day multi-window)
                if (Array.isArray(settings.interviewSchedule)) {
                    const slots = scheduleToSlots(settings.interviewSchedule);
                    setInterviewSlots(slots);
                    setInitialInterviewSlots(JSON.parse(JSON.stringify(slots)));
                } else if (settings.selectedDays) {
                    // Legacy: selectedDays (index array) + single startTime/endTime
                    const st = settings.startTime || '09:00 AM';
                    const et = settings.endTime || '05:00 PM';
                    const slots = settings.selectedDays.map((day) => ({
                        day,
                        startTime: st,
                        endTime: et,
                    }));
                    slots.sort((a, b) => a.day - b.day);
                    setInterviewSlots(slots.length > 0 ? slots : defaultSlots);
                    setInitialInterviewSlots(JSON.parse(JSON.stringify(slots.length > 0 ? slots : defaultSlots)));
                }

                const hrDur = settings.hrDuration || 0;
                const minDur = settings.minDuration || 30;
                const zoneVal = settings.zone || '';
                const confirmMsg = settings.confirmationMsg || '';
                const maxSlot = settings.maxPerInterviewSlot || 1;
                const includeStore = settings.includeStoreLocation || false;
                const schedSeq = settings.scheduleSequence || 'random';
                
                setHrDuration(hrDur);
                setInitialHrDuration(hrDur);
                
                setMinDuration(minDur);
                setInitialMinDuration(minDur);
                
                setZone(zoneVal);
                setInitialZone(zoneVal);
                
                setConfirmationMsg(confirmMsg);
                setInitialConfirmationMsg(confirmMsg);
                
                setMaxPerInterviewSlot(maxSlot);
                setInitialMaxPerSlot(maxSlot);
                
                setIncludeStoreLocation(includeStore);
                setInitialIncludeLocation(includeStore);
                
                setScheduleSequence(schedSeq);
                setInitialScheduleSequence(schedSeq);

                const reminderMsgValue = settings.eventReminderMsg || eventReminderMsg;
                const reminderEnabledValue = settings.eventReminderEnabled !== undefined ? settings.eventReminderEnabled : true;
                const reminderTimeValue = settings.eventReminderTime || 8;
                
                setEventReminderMsg(reminderMsgValue);
                setInitialReminderMsg(reminderMsgValue);
                
                setEventReminderEnabled(reminderEnabledValue);
                setInitialReminderEnabled(reminderEnabledValue);
                
                setEventReminderTime(reminderTimeValue);
                setInitialReminderTime(reminderTimeValue);

                if (settings.noInterviewsWithin && /(\d*[a-zA-Z]{1})/.test(settings.noInterviewsWithin)) {
                    const temp = settings.noInterviewsWithin.split(/(\d+)/);
                    const numVal = parseInt(temp[1]);
                    const periodVal = temp[2];
                    setNoInterviewsWithinNumber(numVal);
                    setInitialNoInterviewsNumber(numVal);
                    setNoInterviewsWithinPeriod(periodVal);
                    setInitialNoInterviewsPeriod(periodVal);
                } else {
                    const numVal = settings.noInterviewsWithin || 0;
                    setNoInterviewsWithinNumber(numVal);
                    setInitialNoInterviewsNumber(numVal);
                }
            }
        } catch (error) {
            message.error('Failed to load settings');
        } finally {
            setLoading(false);
        }
    };

    const loadApps = async () => {
        try {
            const response = await getAllApps('interview-invites');
            setIntegrationApps(response.data);
            setGroups(response.data.groups || []);
        } catch (error) {
            message.error('Failed to load integrations');
        }
    };

    const saveMessagingSettings = async () => {
        if (!inviteMsg) {
            message.error('Interview invite message is required');
            return;
        }

        if (scheduledMessageEnabled) {
            let hasError = false;
            schedmsgs.forEach((schedule, index) => {
                if (schedule.isActive) {
                    if (!schedule.msg || !schedule.sched || schedule.sched < 1 || schedule.sched > 168) {
                        hasError = true;
                    }
                }
            });

            if (hasError) {
                message.error('Please check scheduled messages for errors');
                return;
            }
        }

        if (eventReminderEnabled && eventReminderTime < 1) {
            message.error('Event reminder time must be at least 1 hour');
            return;
        }

        try {
            const contacts = await getSchedContactsAffectedByUserSetting();
            
            if (contacts.data && contacts.data.length > 0) {
                setAffectedContacts(contacts.data);
                setShowAffectedWarningModal(true);
                return;
            }

            await saveSettingsProcedure();
        } catch (error) {
            message.error('Failed to save settings');
        }
    };

    const saveSettingsProcedure = async () => {
        try {
            const response = await getUserSettings();
            const settings = response.data;

            if (settings) {
                let selectedDaysArray = [false, false, false, false, false, false, false];
                if (settings.selectedDays) {
                    settings.selectedDays.forEach(day => selectedDaysArray[day] = true);
                }

                const transformed = schedmsgs.map((schedule, index) => ({
                    ...schedule,
                    key: index + 1,
                    sched: schedule.sched ? `${schedule.sched}h` : ''
                }));

                const data = {
                    ...settings,
                    selectedDays: selectedDaysArray,
                    inviteMsg,
                    noSlotsAvailableMessage,
                    postEventConfirmationResponseMessage,
                    cancelledMsg,
                    scheduledMessageEnabled,
                    schedmsgs: transformed,
                    eventReminderMsg,
                    eventReminderEnabled,
                    eventReminderTime
                };

                delete data.location;

                await setUserSettings(data);
                message.success('Settings saved successfully');
                setShowAffectedWarningModal(false);
            }
        } catch (error) {
            message.error('Failed to save settings');
        }
    };

    const saveCalendarSettings = async () => {
        if (interviewSlots.length === 0) {
            message.error('Please add at least one availability slot');
            return;
        }

        if (hrDuration === 0 && minDuration === 0) {
            message.error('Interview duration must be at least 5 minutes');
            return;
        }

        if (maxPerInterviewSlot < 1) {
            message.error('At least 1 candidate per slot is required');
            return;
        }

        try {
            const interviewSchedule = slotsToSchedule(interviewSlots);
            const legacy = slotsToLegacy(interviewSlots);

            await updateCalendarSettings(currentUserId, {
                selectedDays: legacy.selectedDays,
                startTime: legacy.startTime,
                endTime: legacy.endTime,
                zone,
                scheduleSequence,
                hrDuration,
                minDuration,
                maxPerInterviewSlot,
                noInterviewsWithin: `${noInterviewsWithinNumber}${noInterviewsWithinPeriod}`,
                includeStoreLocation,
                confirmationMsg,
                interviewSchedule,
            });

            // Update initial state after successful save
            setInitialInterviewSlots(JSON.parse(JSON.stringify(interviewSlots)));
            setInitialHrDuration(hrDuration);
            setInitialMinDuration(minDuration);
            setInitialZone(zone);
            setInitialConfirmationMsg(confirmationMsg);
            setInitialMaxPerSlot(maxPerInterviewSlot);
            setInitialIncludeLocation(includeStoreLocation);
            setInitialNoInterviewsNumber(noInterviewsWithinNumber);
            setInitialNoInterviewsPeriod(noInterviewsWithinPeriod);
            setInitialScheduleSequence(scheduleSequence);

            message.success('Calendar settings saved successfully');
        } catch (error) {
            message.error('Failed to save calendar settings');
        }
    };

    const handleRemoveIntegration = async () => {
        if (!integApp) return;

        const { user_id: userId, company_id: companyId, app_id: appId } = integApp;

        try {
            await removeUserIntegration({ userId, companyId, appId });
            await loadApps();
            message.success('Integration removed successfully');
            setShowRemoveModal(false);
            setIntegApp(null);
        } catch (error) {
            message.error('Failed to remove integration');
            setShowRemoveModal(false);
        }
    };

    const handleSetDefault = async (userApp, group) => {
        try {
            await setAppAsDefault({ appDetails: JSON.stringify(userApp), group });
            message.success('App has been set as default');
            await loadApps();
        } catch (error) {
            message.error('Failed to set default app');
        }
    };

    // Individual save handlers for each sub-form
    const saveInviteMessage = async () => {
        if (!inviteMsg) {
            message.error('Interview invite message is required');
            return;
        }

        setLoadingInvite(true);
        try {
            if (!currentUserId) {
                message.error('User ID not found. Please refresh the page.');
                return;
            }

            await updateInviteMessage(currentUserId, { inviteMsg });
            setInitialInviteMsg(inviteMsg);
            message.success('Invite message saved successfully');
        } catch (error) {
            message.error('Failed to save invite message');
        } finally {
            setLoadingInvite(false);
        }
    };

    const resetInviteMessage = () => {
        setInviteMsg(initialInviteMsg);
    };

    const saveNoSlotsMessage = async () => {
        setLoadingNoSlots(true);
        try {
            await updateNoSlotsMessage(currentUserId, { noSlotsAvailableMessage });
            setInitialNoSlotsMsg(noSlotsAvailableMessage);
            message.success('No slots message saved successfully');
        } catch (error) {
            message.error('Failed to save no slots message');
        } finally {
            setLoadingNoSlots(false);
        }
    };

    const resetNoSlotsMessage = () => {
        setNoSlotsAvailableMessage(initialNoSlotsMsg);
    };

    const saveConfirmedMessage = async () => {
        setLoadingConfirmed(true);
        try {
            await updateConfirmedMessage(currentUserId, { postEventConfirmationResponseMessage });
            setInitialConfirmedMsg(postEventConfirmationResponseMessage);
            message.success('Confirmation message saved successfully');
        } catch (error) {
            message.error('Failed to save confirmation message');
        } finally {
            setLoadingConfirmed(false);
        }
    };

    const resetConfirmedMessage = () => {
        setPostEventConfirmationResponseMessage(initialConfirmedMsg);
    };

    const saveCancelledMessage = async () => {
        setLoadingCancelled(true);
        try {
            await updateCancelledMessage(currentUserId, { cancelledMsg });
            setInitialCancelledMsg(cancelledMsg);
            message.success('Cancelled message saved successfully');
        } catch (error) {
            message.error('Failed to save cancelled message');
        } finally {
            setLoadingCancelled(false);
        }
    };

    const resetCancelledMessage = () => {
        setCancelledMsg(initialCancelledMsg);
    };

    const saveFollowupMessages = async () => {
        const allFollowupsDisabled = schedmsgs.every((s) => !s.isActive);
        const effectiveEnabled = scheduledMessageEnabled && !allFollowupsDisabled;

        if (effectiveEnabled) {
            const errors = [];
            schedmsgs.forEach((schedule, index) => {
                if (!schedule.isActive) return;
                const issues = [];
                if (!schedule.msg) issues.push('message is empty');
                if (!schedule.sched || schedule.sched < 1 || schedule.sched > 168) {
                    issues.push("'Send after' must be between 1 and 168 hours");
                }
                if (issues.length > 0) {
                    errors.push(`Scheduled message ${index + 1}: ${issues.join('; ')}`);
                }
            });

            if (errors.length > 0) {
                message.error(errors.join(' • '));
                return;
            }
        }

        setLoadingFollowup(true);
        try {
            const transformed = schedmsgs.map((schedule, index) => ({
                ...schedule,
                key: index + 1,
                sched: schedule.sched ? `${schedule.sched}h` : ''
            }));

            await updateFollowupMessages(currentUserId, {
                scheduledMessageEnabled: effectiveEnabled,
                schedmsgs: transformed
            });

            if (scheduledMessageEnabled !== effectiveEnabled) {
                setScheduledMessageEnabled(effectiveEnabled);
            }
            setInitialFollowupEnabled(effectiveEnabled);
            setInitialSchedmsgs(JSON.parse(JSON.stringify(schedmsgs)));
            message.success('Follow-up messages saved successfully');
        } catch (error) {
            message.error('Failed to save follow-up messages');
        } finally {
            setLoadingFollowup(false);
        }
    };

    const resetFollowupMessages = () => {
        setScheduledMessageEnabled(initialFollowupEnabled);
        setSchedmsgs(JSON.parse(JSON.stringify(initialSchedmsgs)));
    };

    const saveEventReminders = async () => {
        if (eventReminderEnabled && eventReminderTime < 1) {
            message.error('Event reminder time must be at least 1 hour');
            return;
        }

        setLoadingReminder(true);
        try {
            await updateEventReminders(currentUserId, {
                eventReminderEnabled,
                eventReminderTime,
                eventReminderMsg
            });
            
            setInitialReminderEnabled(eventReminderEnabled);
            setInitialReminderTime(eventReminderTime);
            setInitialReminderMsg(eventReminderMsg);
            message.success('Event reminders saved successfully');
        } catch (error) {
            message.error('Failed to save event reminders');
        } finally {
            setLoadingReminder(false);
        }
    };

    const resetEventReminders = () => {
        setEventReminderEnabled(initialReminderEnabled);
        setEventReminderTime(initialReminderTime);
        setEventReminderMsg(initialReminderMsg);
    };

    const saveCalendarSettingsNew = async () => {
        const clientErrors = validateCalendarForm();
        if (Object.keys(clientErrors).length > 0) {
            setCalendarFieldErrors(clientErrors);
            message.error('Please fix the highlighted fields');
            return;
        }
        setCalendarFieldErrors({});

        setLoadingCalendar(true);
        try {
            const interviewSchedule = slotsToSchedule(interviewSlots);
            const legacy = slotsToLegacy(interviewSlots);

            await updateCalendarSettings(currentUserId, {
                selectedDays: legacy.selectedDays,
                startTime: legacy.startTime,
                endTime: legacy.endTime,
                zone,
                scheduleSequence,
                hrDuration,
                minDuration,
                maxPerInterviewSlot,
                noInterviewsWithin: `${noInterviewsWithinNumber}${noInterviewsWithinPeriod}`,
                includeStoreLocation,
                confirmationMsg,
                interviewSchedule,
            });

            // Update initial state after successful save
            setInitialInterviewSlots(JSON.parse(JSON.stringify(interviewSlots)));
            setInitialHrDuration(hrDuration);
            setInitialMinDuration(minDuration);
            setInitialZone(zone);
            setInitialConfirmationMsg(confirmationMsg);
            setInitialMaxPerSlot(maxPerInterviewSlot);
            setInitialIncludeLocation(includeStoreLocation);
            setInitialNoInterviewsNumber(noInterviewsWithinNumber);
            setInitialNoInterviewsPeriod(noInterviewsWithinPeriod);
            setInitialScheduleSequence(scheduleSequence);

            message.success('Calendar settings saved successfully');
        } catch (error) {
            const serverErrors = parseCalendarBackendError(error);
            setCalendarFieldErrors(serverErrors);
            if (serverErrors._generic && Object.keys(serverErrors).length === 1) {
                message.error(serverErrors._generic);
            } else {
                message.error('Please fix the highlighted fields');
            }
        } finally {
            setLoadingCalendar(false);
        }
    };

    const resetCalendarSettings = () => {
        setInterviewSlots(JSON.parse(JSON.stringify(initialInterviewSlots)));
        setHrDuration(initialHrDuration);
        setMinDuration(initialMinDuration);
        setZone(initialZone);
        setConfirmationMsg(initialConfirmationMsg);
        setMaxPerInterviewSlot(initialMaxPerSlot);
        setIncludeStoreLocation(initialIncludeLocation);
        setNoInterviewsWithinNumber(initialNoInterviewsNumber);
        setNoInterviewsWithinPeriod(initialNoInterviewsPeriod);
        setScheduleSequence(initialScheduleSequence);
        setCalendarFieldErrors({});
    };

    // Messaging Tab Content

    const renderMessagingTab = () => {
        // Helper function to create save/reset buttons for each sub-form
        const createActionButtons = (saveHandler, resetHandler, loading, isDirty) => (
            <div style={{ textAlign: 'right', marginTop: 16, display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                <Button 
                    onClick={resetHandler}
                    disabled={!isDirty || loading}
                >
                    Reset
                </Button>
                <Button 
                    type="primary" 
                    onClick={saveHandler} 
                    loading={loading}
                    disabled={!isDirty}
                    style={{ backgroundColor: isDirty ? '#4CAF50' : undefined, borderColor: isDirty ? '#4CAF50' : undefined }}
                >
                    Save
                </Button>
            </div>
        );

        const handleNodeClick = (nodeId) => {
            if (nodeId.startsWith('fu')) {
                setActiveSection('followup');
            } else {
                setActiveSection(nodeId);
            }
        };

        // Dirty checks
        const isInviteDirty = inviteMsg !== initialInviteMsg;
        const isNoSlotsDirty = noSlotsAvailableMessage !== initialNoSlotsMsg;
        const isConfirmedDirty = postEventConfirmationResponseMessage !== initialConfirmedMsg;
        const isCancelledDirty = cancelledMsg !== initialCancelledMsg;
        const isFollowupDirty = scheduledMessageEnabled !== initialFollowupEnabled || 
            JSON.stringify(schedmsgs) !== JSON.stringify(initialSchedmsgs);
        const isReminderDirty = eventReminderEnabled !== initialReminderEnabled || 
            eventReminderTime !== initialReminderTime || 
            eventReminderMsg !== initialReminderMsg;

        return (
            <div className="tab-content">
                <div className="tab-content-inner">
                    <h3 style={{ fontSize: 18, fontWeight: 600, margin: '0 0 4px 0', color: '#263238' }}>Message Settings</h3>
                    <Paragraph type="secondary" style={{ margin: '0 0 24px 0', fontSize: 13 }}>
                        This is the default interview-invite flow. Use the flow overview to see each step—click green nodes to edit the message templates.
                    </Paragraph>
                    
                    <FlowOverview
                        onNodeClick={handleNodeClick}
                        selectedNode={activeSection === 'followup' ? 'fu1' : activeSection}
                        followUpStates={
                            scheduledMessageEnabled
                                ? [0, 1, 2].map((i) => schedmsgs[i]?.isActive !== false)
                                : [false, false, false]
                        }
                        remEnabled={eventReminderEnabled}
                    />
                    
                    {activeSection === 'invite' && (
                        <Card style={{ marginBottom: 8 }} bordered={false}>
                            <Title level={5} style={{ margin: 0 }}>Interview invite message:</Title>
                            <Paragraph type="secondary" style={{ margin: '4px 0 8px 0', fontSize: 13 }}>
                                This message is set as your default interview invite message.
                            </Paragraph>
                            <InterviewMessageInput
                                value={inviteMsg}
                                onChange={(e) => setInviteMsg(e.target.value)}
                                rows={3}
                                showAppend={true}
                                appendContent={constants.interviewInviteAppend}
                            />
                            {createActionButtons(saveInviteMessage, resetInviteMessage, loadingInvite, isInviteDirty)}
                        </Card>
                    )}

                    {activeSection === 'noneavail' && (
                        <Card style={{ marginBottom: 8 }} bordered={false}>
                            <Title level={5} style={{ margin: 0 }}>No slots available message:</Title>
                            <Paragraph type="secondary" style={{ margin: '4px 0 8px 0', fontSize: 13 }}>
                                This message is sent when there are no available slots on your calendar within the next fifteen (15) days.
                            </Paragraph>
                            <InterviewMessageInput
                                value={noSlotsAvailableMessage}
                                onChange={(e) => setNoSlotsAvailableMessage(e.target.value)}
                                rows={3}
                                showAppend={false}
                            />
                            {createActionButtons(saveNoSlotsMessage, resetNoSlotsMessage, loadingNoSlots, isNoSlotsDirty)}
                        </Card>
                    )}

                    {activeSection === 'confirmed' && (
                        <Card style={{ marginBottom: 8 }} bordered={false}>
                            <Title level={5} style={{ margin: 0 }}>Post-Event Confirmation - Response Message:</Title>
                            <Paragraph type="secondary" style={{ margin: '4px 0 8px 0', fontSize: 13 }}>
                                This message is sent when the user has responded 'confirm' to the booking confirmation message.
                            </Paragraph>
                            <InterviewMessageInput
                                value={postEventConfirmationResponseMessage}
                                onChange={(e) => setPostEventConfirmationResponseMessage(e.target.value)}
                                rows={3}
                                showAppend={false}
                            />
                            {createActionButtons(saveConfirmedMessage, resetConfirmedMessage, loadingConfirmed, isConfirmedDirty)}
                        </Card>
                    )}

                    {activeSection === 'cancelled' && (
                        <Card style={{ marginBottom: 8 }} bordered={false}>
                            <Title level={5} style={{ margin: 0 }}>Cancelled Message:</Title>
                            <Paragraph type="secondary" style={{ margin: '4px 0 8px 0', fontSize: 13 }}>
                                A candidate can reply <strong>3</strong> to the booking confirmation or the reminder to cancel.
                                After they confirm with <strong>YES</strong>, the interview is removed from your calendar, you are emailed and notified,
                                and the candidate receives this message. The last line is added automatically so they can pick new times.
                            </Paragraph>
                            <InterviewMessageInput
                                value={cancelledMsg}
                                onChange={(e) => setCancelledMsg(e.target.value)}
                                rows={3}
                                showAppend={true}
                                appendContent={constants.cancelledMessageAppend}
                            />
                            {createActionButtons(saveCancelledMessage, resetCancelledMessage, loadingCancelled, isCancelledDirty)}
                        </Card>
                    )}

                    {activeSection === 'followup' && (
                        <Card style={{ marginBottom: 8 }} bordered={false}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                                <div style={{ display: 'flex', flexDirection: 'column' }}>
                                    <Title level={4} style={{ margin: 0 }}>Follow-up Messages</Title>
                                    <Paragraph type="secondary" style={{ margin: '4px 0 0 0', fontSize: 13 }}>
                                        You can set up automatic follow-up message/s for your interview invites.
                                    </Paragraph>
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                    <Text>{scheduledMessageEnabled ? 'On' : 'Off'}</Text>
                                    <Switch
                                        checked={scheduledMessageEnabled}
                                        onChange={setScheduledMessageEnabled}
                                    />
                                </div>
                            </div>

                            {scheduledMessageEnabled && (
                                <>
                                    <div style={{ marginBottom: 16 }}>
                                        <Text strong>Reminder Trigger</Text>
                                        <Select value="calendar" style={{ width: '100%', marginTop: 8 }} disabled>
                                            <Option value="calendar">Connected Calendar</Option>
                                        </Select>
                                        <Paragraph type="secondary" style={{ marginTop: 8, fontSize: 12 }}>
                                            A follow-up message will only be sent if the candidate hasn't set an interview on your calendar.
                                        </Paragraph>
                                    </div>

                                    {schedmsgs.map((sched, index) => (
                                        <div key={index} style={{ marginBottom: 24, paddingBottom: index < schedmsgs.length - 1 ? 24 : 0, borderBottom: index < schedmsgs.length - 1 ? '1px solid #f0f0f0' : 'none' }}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                                                <Title level={5} style={{ margin: 0 }}>Scheduled message {index + 1}:</Title>
                                                <Switch
                                                    checked={sched.isActive}
                                                    onChange={(checked) => {
                                                        const newSchedmsgs = [...schedmsgs];
                                                        newSchedmsgs[index].isActive = checked;
                                                        setSchedmsgs(newSchedmsgs);
                                                    }}
                                                    checkedChildren="Enabled"
                                                    unCheckedChildren="Disabled"
                                                />
                                            </div>

                                            <div style={{ marginBottom: 16 }}>
                                                <Text style={{ marginRight: 8 }}>Send after:</Text>
                                                <InputNumber
                                                    value={sched.sched}
                                                    onChange={(value) => {
                                                        const newSchedmsgs = [...schedmsgs];
                                                        newSchedmsgs[index].sched = value;
                                                        setSchedmsgs(newSchedmsgs);
                                                    }}
                                                    min={1}
                                                    max={168}
                                                    disabled={!sched.isActive}
                                                    style={{ width: 120 }}
                                                    placeholder="24"
                                                    addonAfter="hours"
                                                />
                                            </div>

                                            <InterviewMessageInput
                                                value={sched.msg}
                                                onChange={(e) => {
                                                    const newSchedmsgs = [...schedmsgs];
                                                    newSchedmsgs[index].msg = e.target.value;
                                                    setSchedmsgs(newSchedmsgs);
                                                }}
                                                rows={3}
                                                disabled={!sched.isActive}
                                                showAppend={true}
                                                appendContent={constants.interviewInviteAppend}
                                            />
                                        </div>
                                    ))}
                            </>
                        )}
                        {createActionButtons(saveFollowupMessages, resetFollowupMessages, loadingFollowup, isFollowupDirty)}
                        </Card>
                    )}

                    {activeSection === 'reminder' && (
                        <Card style={{ marginBottom: 8 }} bordered={false}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                                <Title level={4} style={{ margin: 0 }}>Event Reminders</Title>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                    <Text>{eventReminderEnabled ? 'On' : 'Off'}</Text>
                                    <Switch
                                        checked={eventReminderEnabled}
                                        onChange={setEventReminderEnabled}
                                    />
                                </div>
                            </div>
                            <Paragraph type="secondary" style={{ marginTop: 4, marginBottom: 12, fontSize: 13 }}>
                                Automatically send out reminders to your contacts for upcoming scheduled events.
                            </Paragraph>

                            {eventReminderEnabled && (
                                <>
                                    <div style={{ marginTop: 12 }}>
                                        <Title level={5} style={{ margin: 0 }}>Reminder Scheduling:</Title>
                                        <Paragraph type="secondary" style={{ marginTop: 4, marginBottom: 8, fontSize: 13 }}>
                                            How many hours before the event should we send your contact a reminder? 
                                            The default value is 8 hours, but can be edited.
                                        </Paragraph>
                                        <Row gutter={16}>
                                            <Col span={12}>
                                                <InputNumber
                                                    value={eventReminderTime}
                                                    onChange={setEventReminderTime}
                                                    min={1}
                                                    max={100}
                                                    style={{ width: '100%' }}
                                                    placeholder="8"
                                                    addonAfter="Hours"
                                                />
                                                {eventReminderTime < 1 && (
                                                    <Text type="danger" style={{ display: 'block', marginTop: 8 }}>
                                                        *minimum of one hour.
                                                    </Text>
                                                )}
                                            </Col>
                                        </Row>
                                    </div>

                                    <div style={{ marginTop: 12 }}>
                                        <Title level={5} style={{ margin: 0 }}>Custom Reminder Message:</Title>
                                        <Paragraph type="secondary" style={{ marginTop: 4, marginBottom: 8, fontSize: 13 }}>
                                            Include a customized message on the reminder.
                                        </Paragraph>
                                        <InterviewMessageInput
                                            value={eventReminderMsg}
                                            onChange={(e) => setEventReminderMsg(e.target.value)}
                                            rows={3}
                                            showAppend={true}
                                            appendContent={constants.eventReminderAppend}
                                        />
                                    </div>
                                </>
                            )}
                            {createActionButtons(saveEventReminders, resetEventReminders, loadingReminder, isReminderDirty)}
                        </Card>
                    )}
                </div>
            </div>
        );
    };

    // Calendar Tab Content
    const renderCalendarTab = () => {
        const timeZones = moment.tz.names().map(tz => {
            const offset = moment.tz(tz).format('Z');
            return {
                label: `(GMT${offset}) ${tz}`,
                value: tz
            };
        });

        const dayOptions = [
            { label: 'Sun', value: 0 },
            { label: 'Mon', value: 1 },
            { label: 'Tue', value: 2 },
            { label: 'Wed', value: 3 },
            { label: 'Thu', value: 4 },
            { label: 'Fri', value: 5 },
            { label: 'Sat', value: 6 },
        ];

        // Calendar dirty check
        const isCalendarDirty =
            JSON.stringify(interviewSlots) !== JSON.stringify(initialInterviewSlots) ||
            hrDuration !== initialHrDuration ||
            minDuration !== initialMinDuration ||
            zone !== initialZone ||
            confirmationMsg !== initialConfirmationMsg ||
            maxPerInterviewSlot !== initialMaxPerSlot ||
            includeStoreLocation !== initialIncludeLocation ||
            noInterviewsWithinNumber !== initialNoInterviewsNumber ||
            noInterviewsWithinPeriod !== initialNoInterviewsPeriod ||
            scheduleSequence !== initialScheduleSequence;

        const updateSlot = (index, field, value) => {
            const updated = [...interviewSlots];
            updated[index] = { ...updated[index], [field]: value };
            setInterviewSlots(updated);
            clearCalendarSlotErrors();
        };

        const addSlot = () => {
            setInterviewSlots([...interviewSlots, { day: 1, startTime: '09:00 AM', endTime: '05:00 PM' }]);
            clearCalendarSlotErrors();
        };

        const removeSlot = (index) => {
            const updated = interviewSlots.filter((_, i) => i !== index);
            setInterviewSlots(updated.length > 0 ? updated : [{ day: 1, startTime: '09:00 AM', endTime: '05:00 PM' }]);
            clearCalendarSlotErrors();
        };

        return (
            <div className="tab-content">
                <div className="tab-content-inner">
                    <h3 style={{ fontSize: 18, fontWeight: 600, margin: '0 0 24px 0', color: '#263238' }}>Calendar Settings</h3>

                    {/* Availability Slots */}
                    <div style={{ marginBottom: 24 }}>
                        <Title level={5} style={{ margin: 0, marginBottom: 8 }}>Availability:</Title>
                        <Paragraph type="secondary" style={{ marginTop: 0, marginBottom: 12, fontSize: 13 }}>
                            Set the days and times you are available for interviews. Add multiple rows for the same day to have separate time windows.
                        </Paragraph>
                        {calendarFieldErrors.slotsGeneric && (
                            <Text type="danger" style={{ display: 'block', marginBottom: 8, fontSize: 12 }}>
                                {calendarFieldErrors.slotsGeneric}
                            </Text>
                        )}
                        {calendarFieldErrors.slotsByDay && Object.keys(calendarFieldErrors.slotsByDay).length > 0 && (
                            <div style={{ marginBottom: 8 }}>
                                {Object.entries(calendarFieldErrors.slotsByDay).map(([dayName, msg]) => (
                                    <Text key={dayName} type="danger" style={{ display: 'block', fontSize: 12 }}>
                                        {dayName}: {msg}
                                    </Text>
                                ))}
                            </div>
                        )}
                        <div style={{ background: '#fafafa', borderRadius: 6, border: '1px solid #f0f0f0', padding: 12 }}>
                            {interviewSlots.map((slot, index) => {
                                const rowError = calendarFieldErrors.slotRows && calendarFieldErrors.slotRows[index];
                                return (
                                    <div
                                        key={index}
                                        style={{
                                            padding: '6px 0',
                                            borderBottom: index < interviewSlots.length - 1 ? '1px solid #f0f0f0' : 'none',
                                        }}
                                    >
                                        <div
                                            style={{
                                                display: 'flex',
                                                gap: 8,
                                                alignItems: 'center',
                                            }}
                                        >
                                            <div style={{ width: 120 }}>
                                                {index === 0 && <Text style={{ fontSize: 12, color: '#8c8c8c', display: 'block', marginBottom: 4 }}>Day</Text>}
                                                <Select
                                                    size="small"
                                                    value={slot.day}
                                                    onChange={(v) => updateSlot(index, 'day', v)}
                                                    style={{ width: '100%' }}
                                                    options={dayOptions}
                                                />
                                            </div>
                                            <div>
                                                {index === 0 && <Text style={{ fontSize: 12, color: '#8c8c8c', display: 'block', marginBottom: 4 }}>Start</Text>}
                                                <TimePicker
                                                    size="small"
                                                    use12Hours
                                                    format="h:mm A"
                                                    style={{ width: 120 }}
                                                    allowClear={false}
                                                    status={rowError ? 'error' : undefined}
                                                    value={slot.startTime ? dayjs(slot.startTime, 'h:mm A') : null}
                                                    onChange={(t) => updateSlot(index, 'startTime', t ? t.format('h:mm A') : '')}
                                                />
                                            </div>
                                            <div>
                                                {index === 0 && <Text style={{ fontSize: 12, color: '#8c8c8c', display: 'block', marginBottom: 4 }}>End</Text>}
                                                <TimePicker
                                                    size="small"
                                                    use12Hours
                                                    format="h:mm A"
                                                    style={{ width: 120 }}
                                                    allowClear={false}
                                                    status={rowError ? 'error' : undefined}
                                                    value={slot.endTime ? dayjs(slot.endTime, 'h:mm A') : null}
                                                    onChange={(t) => updateSlot(index, 'endTime', t ? t.format('h:mm A') : '')}
                                                />
                                            </div>
                                            <div style={{ paddingTop: index === 0 ? 20 : 0 }}>
                                                <Button
                                                    size="small"
                                                    type="text"
                                                    danger
                                                    icon={<CloseOutlined />}
                                                    onClick={() => removeSlot(index)}
                                                />
                                            </div>
                                        </div>
                                        {rowError && (
                                            <Text type="danger" style={{ display: 'block', marginTop: 4, marginLeft: 128, fontSize: 12 }}>
                                                {rowError}
                                            </Text>
                                        )}
                                    </div>
                                );
                            })}
                            <Button
                                size="small"
                                type="dashed"
                                onClick={addSlot}
                                style={{ width: '100%', marginTop: 8 }}
                            >
                                + Add Slot
                            </Button>
                        </div>
                    </div>

                    {/* Timezone & Scheduling Type */}
                    <div style={{ marginBottom: 32 }}>
                        <Row gutter={16} style={{ marginBottom: 16 }}>
                            <Col span={12}>
                                <Text style={{ fontSize: 13, color: '#546e7a' }}>Timezone</Text>
                                <Select
                                    showSearch
                                    value={zone}
                                    onChange={(v) => { setZone(v); clearCalendarError('zone'); }}
                                    style={{ width: '100%', marginTop: 4 }}
                                    placeholder="Select timezone"
                                    optionFilterProp="children"
                                    status={calendarFieldErrors.zone ? 'error' : undefined}
                                >
                                    {timeZones.map((tz, idx) => (
                                        <Option key={idx} value={tz.value}>{tz.label}</Option>
                                    ))}
                                </Select>
                                {calendarFieldErrors.zone && (
                                    <Text type="danger" style={{ display: 'block', marginTop: 4, fontSize: 12 }}>
                                        {calendarFieldErrors.zone}
                                    </Text>
                                )}
                            </Col>
                            <Col span={12}>
                                <Text style={{ fontSize: 13, color: '#546e7a' }}>Scheduling Type</Text>
                                <Select
                                    value={scheduleSequence}
                                    onChange={setScheduleSequence}
                                    style={{ width: '100%', marginTop: 4 }}
                                >
                                    <Option value="random">Random (Individuals with varying schedules)</Option>
                                    <Option value="next-available">Next Available (Immediate interview times)</Option>
                                </Select>
                            </Col>
                        </Row>
                    </div>

                    {/* Duration & Capacity */}
                    <div style={{ marginBottom: 32 }}>
                        <Row gutter={16}>
                            <Col span={12}>
                                <Title level={5} style={{ margin: 0, marginBottom: 4 }}>Interview Duration</Title>
                                <Paragraph type="secondary" style={{ marginTop: 0, marginBottom: 8, fontSize: 12 }}>
                                    Set the number of minutes for your interview.
                                </Paragraph>
                                <div style={{ display: 'flex', gap: 8 }}>
                                    <InputNumber
                                        value={hrDuration}
                                        onChange={(v) => {
                                            setHrDuration(v);
                                            clearCalendarError('hrDuration');
                                            clearCalendarError('duration');
                                        }}
                                        min={0}
                                        max={8}
                                        style={{ flex: 1 }}
                                        addonAfter="hrs"
                                        status={(calendarFieldErrors.hrDuration || calendarFieldErrors.duration) ? 'error' : undefined}
                                    />
                                    <InputNumber
                                        value={minDuration}
                                        onChange={(v) => {
                                            setMinDuration(v);
                                            clearCalendarError('minDuration');
                                            clearCalendarError('duration');
                                        }}
                                        min={0}
                                        max={60}
                                        step={5}
                                        style={{ flex: 1 }}
                                        addonAfter="mins"
                                        status={(calendarFieldErrors.minDuration || calendarFieldErrors.duration) ? 'error' : undefined}
                                    />
                                </div>
                                {calendarFieldErrors.hrDuration && (
                                    <Text type="danger" style={{ display: 'block', marginTop: 4, fontSize: 12 }}>
                                        Hours: {calendarFieldErrors.hrDuration}
                                    </Text>
                                )}
                                {calendarFieldErrors.minDuration && (
                                    <Text type="danger" style={{ display: 'block', marginTop: 4, fontSize: 12 }}>
                                        Minutes: {calendarFieldErrors.minDuration}
                                    </Text>
                                )}
                                {calendarFieldErrors.duration && !calendarFieldErrors.hrDuration && !calendarFieldErrors.minDuration && (
                                    <Text type="danger" style={{ display: 'block', marginTop: 4, fontSize: 12 }}>
                                        {calendarFieldErrors.duration}
                                    </Text>
                                )}
                            </Col>
                             <Col span={12}>
                                <Title level={5} style={{ margin: 0, marginBottom: 4 }}>Candidates per slot</Title>
                                <Paragraph type="secondary" style={{ marginTop: 0, marginBottom: 8, fontSize: 12 }}>
                                    Max candidates per time slot.
                                </Paragraph>
                                <InputNumber
                                    value={maxPerInterviewSlot}
                                    onChange={(v) => { setMaxPerInterviewSlot(v); clearCalendarError('maxPerInterviewSlot'); }}
                                    min={1}
                                    max={100}
                                    style={{ width: '100%' }}
                                    status={calendarFieldErrors.maxPerInterviewSlot ? 'error' : undefined}
                                />
                                {calendarFieldErrors.maxPerInterviewSlot && (
                                    <Text type="danger" style={{ display: 'block', marginTop: 4, fontSize: 12 }}>
                                        {calendarFieldErrors.maxPerInterviewSlot}
                                    </Text>
                                )}
                            </Col>
                        </Row>
                    </div>

                    {/* Constraints */}
                    <div style={{ marginBottom: 32 }}>
                         <Title level={5} style={{ margin: 0, marginBottom: 8 }}>Candidates cannot schedule interviews in:</Title>
                         <Paragraph type="secondary" style={{ marginTop: 0, marginBottom: 12, fontSize: 13 }}>
                            Prevents booking within X time from now.
                        </Paragraph>
                        <Row gutter={16}>
                            <Col span={12}>
                                <InputNumber
                                    value={noInterviewsWithinNumber}
                                    onChange={(v) => { setNoInterviewsWithinNumber(v); clearCalendarError('noInterviewsWithinNumber'); }}
                                    min={0}
                                    max={100}
                                    style={{ width: '100%' }}
                                    placeholder="1"
                                    status={calendarFieldErrors.noInterviewsWithinNumber ? 'error' : undefined}
                                />
                                {calendarFieldErrors.noInterviewsWithinNumber && (
                                    <Text type="danger" style={{ display: 'block', marginTop: 4, fontSize: 12 }}>
                                        {calendarFieldErrors.noInterviewsWithinNumber}
                                    </Text>
                                )}
                            </Col>
                            <Col span={12}>
                                <Select
                                    value={noInterviewsWithinPeriod}
                                    onChange={setNoInterviewsWithinPeriod}
                                    style={{ width: '100%' }}
                                >
                                    <Option value="d">Days</Option>
                                    <Option value="h">Hours</Option>
                                    <Option value="m">Minutes</Option>
                                </Select>
                            </Col>
                        </Row>
                    </div>

                    {/* Location */}
                    <div style={{ marginBottom: 32 }}>
                        <Checkbox
                            checked={includeStoreLocation}
                            onChange={(e) => setIncludeStoreLocation(e.target.checked)}
                        >
                            <span style={{ fontWeight: 600, fontSize: 15 }}>Include store location on email</span>
                        </Checkbox>
                         <Paragraph type="secondary" style={{ marginTop: 4, marginLeft: 24, fontSize: 13 }}>
                            Check this only if you are performing on-site interviews.
                        </Paragraph>
                    </div>

                     {/* Additional Email Content */}
                    <div style={{ marginBottom: 32 }}>
                        <Title level={5} style={{ margin: 0, marginBottom: 8 }}>Additional email content:</Title>
                        <Paragraph type="secondary" style={{ marginTop: 0, marginBottom: 12, fontSize: 13 }}>
                            Include a customized interview confirmation email.
                        </Paragraph>
                        <InterviewMessageInput
                            value={confirmationMsg}
                            onChange={(e) => setConfirmationMsg(e.target.value)}
                            rows={3}
                            showAppend={false}
                            placeholder="Additional Email content"
                        />
                    </div>

                    <div style={{ textAlign: 'right', marginTop: 24, display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                        <Button 
                            size="large"
                            onClick={resetCalendarSettings}
                            disabled={!isCalendarDirty || loadingCalendar}
                        >
                            Reset
                        </Button>
                        <Button
                            type="primary"
                            size="large"
                            onClick={saveCalendarSettingsNew}
                            loading={loadingCalendar}
                            disabled={!isCalendarDirty}
                            style={{ backgroundColor: isCalendarDirty ? '#4CAF50' : undefined, borderColor: isCalendarDirty ? '#4CAF50' : undefined }}
                        >
                            Save
                        </Button>
                    </div>
                </div>
            </div>
        );
    };

    // Integrations Tab Content
    const renderIntegrationsTab = () => (
        <div className="tab-content">
            <div className="tab-content-inner" style={{ maxWidth: '100%' }}>
                <h3 style={{ fontSize: 18, fontWeight: 600, margin: '0 0 8px 0', color: '#263238' }}>Integration Settings</h3>
                
                <Paragraph style={{ marginBottom: 24, color: '#90A4AE' }}>
                    Connect and manage third-party services to enhance your workflow
                </Paragraph>

                <Row gutter={[16, 16]}>
                    {groups.map(group => {
                        // Filter out Calendly validation
                        const apps = group.apps.filter(app => app.type !== 'calendly');
                        if (apps.length === 0) return null;

                        // Format group label
                        let sectionTitle = group.label;
                        const isCalendarGroup = group.group === 'calendar' || group.label.toLowerCase() === 'calendar';
                        
                        if (sectionTitle.toLowerCase() === 'ats hris') sectionTitle = 'ATS & HRIS';
                        if (isCalendarGroup) sectionTitle = 'Calendars';

                        return (
                            <Col span={24} key={group.group} style={{ marginBottom: 24 }}>
                                <Title level={4} style={{ fontSize: 16, fontWeight: 600, marginBottom: 16, color: '#455A64' }}>
                                    {sectionTitle}
                                </Title>
                                <Row gutter={[16, 16]}>
                                    {apps.map(app => {
                                        const userApp = integrationApps.companyApps?.find(ua => ua.app_id === app.id);
                                        const isConnected = userApp && !userApp.app.auth_error;
                                        const hasError = userApp && userApp.app.auth_error;
                                        
                                        // Generate Connection Info
                                        let connectionInfo = '';
                                        if (isConnected) {
                                            const calendarDetails = userApp.app.calendar_details;
                                            if (calendarDetails) {
                                                connectionInfo = `${calendarDetails.calendar_name || ''} ${calendarDetails.profile_name ? `on ${calendarDetails.profile_name}` : ''}`;
                                            }
                                        }

                                        // Get description
                                        const appKey = app.image?.split('.')[0] || app.name.toLowerCase();
                                        const defaultDesc = appDescriptions[appKey] || appDescriptions[app.name.toLowerCase()] || 'Connect and manage this service to enhance your workflow.';
                                        const description = (isConnected && connectionInfo) ? connectionInfo : defaultDesc;

                                        return (
                                            <Col xs={24} sm={12} md={12} lg={6} xl={6} key={app.id}>
                                                <Card 
                                                    hoverable
                                                    bordered
                                                    style={{ 
                                                        height: '100%', 
                                                        borderRadius: '8px',
                                                        borderColor: '#E6E6E6',
                                                        display: 'flex',
                                                        flexDirection: 'column' 
                                                    }}
                                                    bodyStyle={{
                                                        padding: '16px',
                                                        height: '100%',
                                                        display: 'flex',
                                                        flexDirection: 'column'
                                                    }}
                                                >
                                                    <div style={{ marginBottom: 12 }}>
                                                        <img
                                                            src={integrationLogos[app.image] || integrationLogos[app.image?.split('.')[0]]}
                                                            alt={app.name}
                                                            style={{ width: 32, height: 32, objectFit: 'contain', borderRadius: '6px' }}
                                                        />
                                                    </div>
                                                    
                                                    <Title level={5} style={{ marginBottom: 6, fontSize: 14, fontWeight: 700 }}>
                                                        {app.name}
                                                    </Title>
                                                    
                                                    <Paragraph type={isConnected && connectionInfo ? "primary" : "secondary"} style={{ fontSize: 12, color: isConnected && connectionInfo ? '#263238' : '#666', lineHeight: '18px', flex: 1, marginBottom: 16, minHeight: 36, fontWeight: isConnected && connectionInfo ? 500 : 400 }}>
                                                        {description}
                                                    </Paragraph>
                                                    
                                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 'auto' }}>
                                                        {isConnected ? (
                                                            isCalendarGroup ? (
                                                                userApp.is_default ? (
                                                                    <Tag color="success" style={{ marginRight: 0, fontSize: 11, lineHeight: '20px' }}>Default</Tag>
                                                                ) : (
                                                                    <Button 
                                                                        type="text" 
                                                                        size="small"
                                                                        style={{ padding: 0, fontSize: 12, color: '#546E7A', fontWeight: 500 }}
                                                                        onClick={() => handleSetDefault(userApp, group.group)}
                                                                    >
                                                                        Set As Default
                                                                    </Button>
                                                                )
                                                            ) : (
                                                                <Text style={{ color: '#00C853', fontWeight: 600, fontSize: 12 }}>Connected</Text>
                                                            )
                                                        ) : hasError ? (
                                                            <Text style={{ color: '#f5222d', fontWeight: 600, fontSize: 12 }}>Connection Error</Text>
                                                        ) : null}

                                                        <Switch
                                                            size="small"
                                                            checked={!!isConnected}
                                                            onChange={(checked) => {
                                                                if (checked) {
                                                                    if (appKey === 'atsconnect') {
                                                                        setShowComingSoonModal(true);
                                                                        return;
                                                                    }
                                                                    window.location.href = app.url;
                                                                } else {
                                                                    if (userApp) {
                                                                        setIntegApp(userApp);
                                                                        setShowRemoveModal(true);
                                                                    }
                                                                }
                                                            }}
                                                        />
                                                    </div>
                                                </Card>
                                            </Col>
                                        );
                                    })}
                                </Row>
                            </Col>
                        );
                    })}
                </Row>
            </div>
        </div>
    );

    const tabItems = [
        {
            key: 'messaging',
            label: 'Message Settings',
            children: renderMessagingTab()
        },
        {
            key: 'calendar',
            label: 'Calendar Settings',
            children: renderCalendarTab()
        },
        {
            key: 'integrations',
            label: 'Integrations',
            children: renderIntegrationsTab()
        }
    ];

    return (
        <div className="interview-invites-wrapper" style={{ minHeight: '100%', display: 'flex', flexDirection: 'column', backgroundColor: '#FAFAFA' }}>
                       
            <div
                style={{
                    background: 'linear-gradient(110deg, #F6FFED 0%, #E6F7FF 100%)',
                    border: '1px solid #d9f7be',
                    padding: 20
                }}
            >
                <Row gutter={[16, 16]} align="middle" justify="space-between">
                    <Col xs={24} md={16}>
                        <Space direction="vertical" size={6}>
                            <Title level={3} style={{ margin: 0, color: '#1f2937' }}>Interview Invites <PageTourButton steps={getInterviewInvitesSteps()} /></Title>
                            <Text style={{ fontSize: 15, color: '#4b5563' }}>
                                Automate the scheduling process by sending interview invites and tracking candidate responses in real-time.
                            </Text>
                            <Space size={16}>
                                <Space size={6}>
                                    <CalendarOutlined style={{ color: '#08979c' }} />
                                    <Text type="secondary">Automated scheduling</Text>
                                </Space>
                                <Space size={6}>
                                    <RocketOutlined style={{ color: '#08979c' }} />
                                    <Text type="secondary">Accelerated hiring</Text>
                                </Space>
                            </Space>
                        </Space>
                    </Col>
                    <Col xs={24} md={8} style={{ textAlign: 'right' }}>
                        {/* Actions if any */}
                    </Col>
                </Row>
            </div>
                       

            <div style={{ flex: 1, margin: '0', backgroundColor: 'white', border: '1px solid #E6E6E6' }}>
                <Tabs
                    className="tour-interview-tabs"
                    activeKey={activeTab}
                    onChange={handleTabChange}
                    tabPosition="left"
                    items={tabItems}
                    style={{ minHeight: '100%' }}
                />
            </div>

            <Modal
                title="Affected Contacts Warning"
                open={showAffectedWarningModal}
                onOk={saveSettingsProcedure}
                onCancel={() => setShowAffectedWarningModal(false)}
                okText="Submit"
                cancelText="Cancel"
            >
                <Paragraph>
                    Queued follow-up messages for the following contact(s) may be affected:
                </Paragraph>
                <ul>
                    {affectedContacts.map((contact, idx) => (
                        <li key={idx}>{contact.name}</li>
                    ))}
                </ul>
            </Modal>

            <Modal
                title="Remove Integration"
                open={showRemoveModal}
                onOk={handleRemoveIntegration}
                onCancel={() => {
                    setShowRemoveModal(false);
                    setIntegApp(null);
                }}
                okText="Yes"
                cancelText="No"
                okButtonProps={{ danger: true }}
            >
                <Paragraph>
                    Are you sure you want to remove {integApp ? integApp.app.name : ''} integration?
                </Paragraph>
                {integApp && integApp.app.type === 'merge' && (
                    <Alert
                        message="Caution"
                        description="If you want to reintegrate, you will have to go through the initial sync sequence again."
                        type="warning"
                        showIcon
                    />
                )}
            </Modal>

            <Modal
                title={null}
                open={showComingSoonModal}
                onCancel={() => setShowComingSoonModal(false)}
                footer={null}
                width={520}
                centered
            >
                <div style={{ textAlign: 'center', padding: '12px 8px 4px' }}>
                    <div
                        style={{
                            width: 72,
                            height: 72,
                            borderRadius: 16,
                            margin: '0 auto 20px',
                            background: 'linear-gradient(135deg, #0891B2 0%, #0E7490 100%)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            boxShadow: '0 8px 24px rgba(8, 145, 178, 0.25)'
                        }}
                    >
                        <ApiOutlined style={{ fontSize: 34, color: '#fff' }} />
                    </div>
                    <Tag color="cyan" style={{ borderRadius: 12, padding: '2px 10px', fontWeight: 500 }}>
                        Coming soon
                    </Tag>
                    <Title level={3} style={{ marginTop: 12, marginBottom: 8 }}>
                        One-click sync with 50+ ATS &amp; HRIS platforms
                    </Title>
                    <Paragraph style={{ color: '#546E7A', fontSize: 14, marginBottom: 20 }}>
                        Stop bouncing between tools. GoHire is launching unified ATS &amp; HRIS
                        connectors so your candidates, jobs, and hires flow automatically between
                        GoHire and the system your team already uses — no CSV exports, no copy-paste.
                    </Paragraph>

                    <div
                        style={{
                            textAlign: 'left',
                            background: '#F6FBFC',
                            border: '1px solid #E0F2F4',
                            borderRadius: 10,
                            padding: '14px 18px',
                            marginBottom: 8
                        }}
                    >
                        <Space direction="vertical" size={8} style={{ width: '100%' }}>
                            <Text style={{ fontSize: 13 }}>
                                <RocketOutlined style={{ color: '#0891B2', marginRight: 8 }} />
                                Auto-sync candidates into your ATS the moment they reply
                            </Text>
                            <Text style={{ fontSize: 13 }}>
                                <CalendarOutlined style={{ color: '#0891B2', marginRight: 8 }} />
                                Pull open jobs &amp; stages straight from Greenhouse, Workday, BambooHR and more
                            </Text>
                            <Text style={{ fontSize: 13 }}>
                                <InfoCircleOutlined style={{ color: '#0891B2', marginRight: 8 }} />
                                Two-way status updates so your pipeline is always in sync
                            </Text>
                        </Space>
                    </div>
                </div>
            </Modal>

            <CronofyCalendarModal
                open={showCronofyModal}
                onCancel={() => setShowCronofyModal(false)}
                calendars={calendars}
                cronofyData={cronofyData}
                onSuccess={() => {
                    setShowCronofyModal(false);
                    loadApps();
                }}
            />
        </div>
    );
};

const mapStateToProps = (state) => {
    return {};
};

const mapDispatchToProps = (dispatch) => {
    return {};
};

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(InterviewInvitesUnified));
